import { canPickEnvironment } from './app-mode.js';

// The public ledger network a company is on. It is chosen once, when the company's account is created, and never changes.
export type LedgerNetwork = 'TESTNET' | 'MAINNET';

export const LEDGER_NETWORKS: readonly LedgerNetwork[] = ['MAINNET', 'TESTNET'];

export function isLedgerNetwork(value: unknown): value is LedgerNetwork {
  return typeof value === 'string' && (LEDGER_NETWORKS as readonly string[]).includes(value);
}

export interface LedgerNetworkPickerState {
  // The app's mode (`settings.app.mode`): production never offers a choice, it is always the live network
  appMode?: string | null;
  // The person's director claim (`people.director`); only `'UNDER_REVIEW'` or `'VERIFIED'` may set the company up
  director?: string | null;
  // `company.ledgerCred.network`, once stored
  companyNetwork?: string | null;
  // Whether the company already has its public ledger account (`ledgerCred.accountId`)
  companyHasAccount: boolean;
}

// The director claims the server lets set the company up (create-account's COMPANY_ACCOUNT_DIRECTOR_STATUSES)
const COMPANY_SETUP_DIRECTOR_STATUSES: readonly string[] = ['UNDER_REVIEW', 'VERIFIED'];

/**
 * Whether the create-account card offers the company-level network choice: outside production only, to the director (claim
 * under review or verified, as the server requires) who creates the company's account (the first one), while the company
 * has no network stored yet. Everyone else's account goes on the company's network, which the server resolves.
 */
export function showLedgerNetworkPicker(state: LedgerNetworkPickerState): boolean {
  return (
    canPickEnvironment(state.appMode) &&
    !!state.director &&
    COMPANY_SETUP_DIRECTOR_STATUSES.includes(state.director) &&
    !isLedgerNetwork(state.companyNetwork) &&
    !state.companyHasAccount
  );
}

export type LedgerExplorerKind = 'account' | 'topic' | 'transaction' | 'schedule';

// The network's name for people to read; the network itself is never named after the ledger's vendor
export function ledgerNetworkLabel(network: LedgerNetwork): string {
  return network === 'MAINNET' ? 'Mainnet' : 'Testnet';
}

/** The public ledger explorer page of an account, topic, transaction or schedule on a network (the lambdas' hashscanUrl). */
export function ledgerExplorerUrl(network: LedgerNetwork, kind: LedgerExplorerKind, id: string): string {
  return `https://hashscan.io/${network === 'MAINNET' ? 'mainnet' : 'testnet'}/${kind}/${encodeURIComponent(id)}`;
}

// Where free testnet funds come from. There is no faucet for mainnet, so it is offered for TESTNET only
export const TESTNET_FAUCET_URL = 'https://portal.hedera.com/faucet';

export function ledgerFaucetUrl(network: LedgerNetwork): string | null {
  return network === 'TESTNET' ? TESTNET_FAUCET_URL : null;
}
